/**
 * scs-session-capture.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Lightweight session-capture snippet for SCS Luxury Seat Covers.
 *
 * Install on EVERY page of the Shopify store (theme.liquid, inside <head>):
 *   <script src="{{ 'scs-session-capture.js' | asset_url }}" defer></script>
 *
 * Session model (matches GA4 / Segment behaviour)
 * ────────────────────────────────────────────────
 *  A session is a continuous period of activity.
 *  It EXPIRES when the user has been inactive for SESSION_TIMEOUT_MS (30 min).
 *  On expiry a brand-new session UUID is created and UTM params are refreshed
 *  from the current URL (so a return visit from a different ad campaign gets
 *  its own attribution record automatically).
 *
 * Storage — a single JSON object in localStorage under "scs_session"
 * ──────────────────────────────────────────────────────────────────
 *  {
 *    id:          string,   // UUID v4
 *    created_at:  string,   // ISO – when this session started
 *    last_seen:   string,   // ISO – updated on every page view (timeout anchor)
 *    url_params:  object    // UTM + click-IDs captured at session start
 *  }
 *
 *  Why localStorage (not sessionStorage)?
 *  • sessionStorage is tab-scoped: a link opened in a new tab has NO attribution.
 *  • localStorage + inactivity timeout is the industry standard (GA4, Segment,
 *    Amplitude all work this way).
 *
 *  Legacy keys written for backward-compatibility with the React SessionService:
 *    localStorage – scs_session_id
 *    localStorage – scs_session_created_at
 *
 *  Global window properties exposed after init:
 *    window.scs_session_id  – the current session UUID (string)
 *    window.scs_session     – the full session store object (read-only)
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
  'use strict';

  /* ── Configuration ──────────────────────────────────────────────────────── */

  var SESSION_STORE_KEY  = 'scs_session';           // primary storage key
  var SESSION_TIMEOUT_MS = 30 * 60 * 1000;          // 30-minute inactivity timeout
  var API_URL            = 'https://scsbiz3.wearevisum.com/api/v1/sessions';

  /**
   * All URL parameters captured for attribution.
   * UTM standard params + major paid-ad click IDs.
   */
  var TRACKED_PARAMS = [
    // Standard UTM
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    // Google Ads (auto-tagging)
    'gclid',
    // Google Display & Video 360
    'dclid',
    // Google Search Ads 360
    'gad_source',
    // Meta / Facebook Ads
    'fbclid',
    // TikTok Ads
    'ttclid',
    // Microsoft / Bing Ads
    'msclkid',
    // LinkedIn Ads (first-party)
    'li_fat_id',
    // Twitter / X Ads
    'twclid',
    // Snapchat Ads
    'ScCid',
    // Pinterest Ads
    'epik',
    // Klaviyo email
    '_kx',
    // Outbrain
    'obclid',
    // Taboola
    'tblci',
  ];

  /* ── Storage helpers ────────────────────────────────────────────────────── */

  function safeLocalGet(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }

  function safeLocalSet(key, val) {
    try { localStorage.setItem(key, val); } catch (e) { /* storage blocked */ }
  }

  function safeSessionGet(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  }

  function safeSessionRemove(key) {
    try { sessionStorage.removeItem(key); } catch (e) { /* storage blocked */ }
  }

  /**
   * The real entry referrer, resolved once at load time.
   *
   * snippets/elevateab.liquid stashes document.referrer into sessionStorage
   * before its (metafield-controlled, synchronous) redirect script can run —
   * this script is `defer`red, so on a page ElevateAB redirects away from,
   * it never runs there at all; by the time it runs on the landing page,
   * document.referrer has already been overwritten to this site's own
   * product page. Prefer that stash (cleared immediately, one-time use) and
   * fall back to the live document.referrer when there's nothing stashed
   * (the common case — no redirect happened).
   */
  var ENTRY_REFERRER = (function () {
    var stashed = safeSessionGet('scs_entry_referrer');
    if (stashed !== null) {
      safeSessionRemove('scs_entry_referrer');
      return stashed;
    }
    return document.referrer || '';
  }());

  function readStore() {
    try {
      var raw = safeLocalGet(SESSION_STORE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  /**
   * Persist the session object and keep legacy individual keys in sync
   * so the React SessionService can still read scs_session_id / scs_session_created_at.
   */
  function writeStore(session) {
    try {
      safeLocalSet(SESSION_STORE_KEY, JSON.stringify(session));
      // Legacy key compatibility
      safeLocalSet('scs_session_id', session.id);
      safeLocalSet('scs_session_created_at', session.created_at);
    } catch (e) { /* non-fatal */ }
  }

  /* ── UUID ───────────────────────────────────────────────────────────────── */

  function generateUUID() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // RFC-4122 v4 fallback (older Safari / Samsung Browser)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  /* ── URL param capture ──────────────────────────────────────────────────── */

  /** Reads all tracked params present in the current URL. */
  function readParamsFromUrl() {
    var result = {};
    try {
      var sp = new URLSearchParams(window.location.search);
      TRACKED_PARAMS.forEach(function (key) {
        var val = sp.get(key);
        if (val) result[key] = val;
      });
    } catch (e) { /* URLSearchParams unavailable in very old browsers */ }
    return result;
  }

  /* ── Session lifecycle ──────────────────────────────────────────────────── */

  function isExpired(session) {
    if (!session || !session.last_seen) return true;
    return (Date.now() - new Date(session.last_seen).getTime()) > SESSION_TIMEOUT_MS;
  }

  /**
   * Resolve (or create) the current session:
   *  - Existing & not expired → update last_seen, merge any new URL params
   *    (first-touch within the session: existing values are not overwritten).
   *  - Expired or absent      → create a new session; capture params fresh
   *    from the current URL (new campaign attribution for new session).
   */
  function resolveSession() {
    var now      = new Date().toISOString();
    var existing = readStore();

    if (!existing || isExpired(existing)) {
      // ── New session ────────────────────────────────────────────────────
      var session = {
        id:         generateUUID(),
        created_at: now,
        last_seen:  now,
        url_params: readParamsFromUrl(),
      };
      writeStore(session);
      return session;
    }

    // ── Continuing session ────────────────────────────────────────────────
    // Update activity timestamp
    existing.last_seen = now;

    // First-touch within session: add params not yet seen (e.g. user browsed
    // from an organic page and later clicked a retargeting ad in the same session)
    var fromUrl = readParamsFromUrl();
    TRACKED_PARAMS.forEach(function (key) {
      if (fromUrl[key] && !existing.url_params[key]) {
        existing.url_params[key] = fromUrl[key];
      }
    });

    writeStore(existing);
    return existing;
  }

  /* ── Payload builder ────────────────────────────────────────────────────── */

  function buildPayload(session) {
    var payload = {
      session_id:  session.id,
      created_at:  session.created_at,
      updated_at:  new Date().toISOString(),
      page_url:    window.location.href,
      referrer:    ENTRY_REFERRER,
      user_agent:  navigator.userAgent || '',
      actions: [
        {
          type:      'page_view',
          timestamp: new Date().toISOString(),
          payload: {
            url:      window.location.href,
            referrer: ENTRY_REFERRER,
            title:    document.title    || '',
          },
        },
      ],
    };

    // Flatten UTM / click-ID params as top-level fields (matches API schema)
    TRACKED_PARAMS.forEach(function (key) {
      if (session.url_params[key]) payload[key] = session.url_params[key];
    });

    if (window.scs_part_number_skus === true) {
      payload.scs_part_number_skus = true;
    }

    return payload;
  }

  /* ── Network ────────────────────────────────────────────────────────────── */

  function postPayload(payload) {
    var body = JSON.stringify(payload);

    if (typeof fetch === 'function') {
      fetch(API_URL, {
        method:      'POST',
        headers:     { 'Content-Type': 'application/json' },
        body:        body,
        credentials: 'omit',
        keepalive:   true, // request survives page navigation in modern browsers
      }).catch(function () { /* non-fatal */ });
      return;
    }

    // XHR fallback (Safari < 17, older Android WebView)
    try {
      var xhr = new XMLHttpRequest();
      xhr.open('POST', API_URL, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.send(body);
    } catch (e) { /* non-fatal */ }
  }

  function beaconPayload(session) {
    try {
      if (typeof navigator.sendBeacon === 'function') {
        // Re-resolve so last_seen is fresh before the tab closes
        var s = readStore() || session;
        navigator.sendBeacon(
          API_URL,
          new Blob([JSON.stringify(buildPayload(s))], { type: 'application/json' })
        );
      }
    } catch (e) { /* non-fatal */ }
  }

  /* ── Init ───────────────────────────────────────────────────────────────── */

  function init() {
    var session = resolveSession();

    // Send page-view immediately
    postPayload(buildPayload(session));

    // Beacon on exit — resolveSession() inside beaconPayload ensures last_seen
    // is current even if the user navigated within the tab before closing
    window.addEventListener('pagehide', function () {
      beaconPayload(session);
    });

    window.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        beaconPayload(session);
      }
    });

    // Track checkout button clicks (rebuy cart)
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest('.rebuy-cart__checkout-button')) {
        var s = readStore() || session;
        var payload = buildPayload(s);
        payload.actions = [
          {
            type:      'checkout_click',
            timestamp: new Date().toISOString(),
            payload: {
              url:   window.location.href,
              title: document.title || '',
            },
          },
        ];
        postPayload(payload);
      }
    });

    // Expose for other scripts (React widget reads window.scs_session_id)
    window.scs_session_id = session.id;
    window.scs_session    = session; // full object available if needed
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

}());

